import React, { useMemo, useEffect, Fragment, useState, useRef, } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { merge, isError, chunk, isEmpty, pickBy, pick, get, keyBy, omit, mapValues, debounce, } from 'lodash';
import { useToggle, useList, } from 'react-use';
import { addDays, } from 'date-fns';
import { toast, } from 'react-toastify';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import numeral from 'numeral';

import firebase, { functions } from '../../firebase';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useFormState from '../hooks/useFormState';
import Field from '../Field';
import { getAddressByPostalCode, } from '../../util';
import { batch, getCollectionData, getDocumentData, } from '../../shared/firebase';
import { deliveryTimes, } from '../../shared/config';
import { computeInboundShipmentErrorSkus, types, fields as _fields, adminFields, itemFields, shippingItemFields, deliveryType, } from '../../shared/models/shippingRequest';
import { computeInventory, } from '../../shared/models/product';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import ListForm from '../ListForm';
import ShippingItemForm from '../forms/ShippingItemForm';
import ModelFormModal from '../modals/ModelFormModal';
import MenuList from '../MenuList';
import ModalButton from '../ModalButton';
import ProgressButton from '../ProgressButton';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import OverlayLoading from '../OverlayLoading';

const db = firebase.firestore();
const fetchFulfillmentInboundShipmentPlan = functions.httpsCallable('fetchFulfillmentInboundShipmentPlan', { timeout: 550000, });
const computeAmazonShippingRequestAllItems = functions.httpsCallable('computeAmazonShippingRequestAllItems', { timeout: 550000, });
const { entries, } = Object;

export default function ShippingRequestFormModal(props) {
  const { isOpen, senders, tenant, newItems, onClickClose, admin = false, onlyShippingItems = false, } = props;
  const initialValuesRef = useRef(props.values);
  const values = initialValuesRef.current;
  const uncompletedShippingRequests = useCollectionSubscription(tenant && tenant.ref.collection('shippingRequests').where('status', 'not-in', ['shipped', 'discarded', 'cancelled']).orderBy('status'), [tenant]);
  const sendersById = keyBy(senders, 'id');
  const isNew = !values || !values.id;
  const type = values.type || 'normal';
  const [selectedExtraSellerId, setSelectedExtraSellerId] = useState(null);
  const extraSellers = useCollectionSubscription(type === 'amazon' && tenant.tenantOptionValues?.includes('multiSellers') && tenant.ref.collection('extraSellers').orderBy('createdAt'), [tenant.id]);
  const extraSellersById = keyBy(extraSellers, 'id');
  const extraSellerOptions = [{ ...tenant, id: null, name: 'メイン' }, ...extraSellers].map(_ => ({ value: _.id, label: _.name, data: _, }));
  const selectedSeller = extraSellerOptions.find(_ => _.value === selectedExtraSellerId).data;
  const products = useMemo(_ => (
    type === 'amazon' ? props.products.filter(_ => _.extraSellerId === selectedExtraSellerId && !_.hides) : props.products.filter(_ => !_.hides)
  ), [type, props.products, selectedExtraSellerId]);
  const fields = admin ? adminFields : _fields;
  const _productsById = keyBy(products, 'id');
  const productsBySku = keyBy(products, 'sku');
  const statedFields = useFormState(values, omit(fields({ isNew, originalStatus: values?.status, tenant, senders, type, }), !isNew && ['files']), isOpen);
  const { items: extraSellerCaseProducts, } = useCollectionSubscription(type === 'amazon' && tenant.ref.collection('products').where('extraSellerId', '==', selectedExtraSellerId).where('isCase', '==', true), [tenant, selectedExtraSellerId], { detail: true });
  const extraSellerCaseProductsById = keyBy(extraSellerCaseProducts, 'id');
  const productsById = merge({}, _productsById, extraSellerCaseProductsById);
  const destinations = useCollectionSubscription(tenant.ref.collection('destinations').orderBy('createdAt'), [tenant]);
  const generalSetting = useDocumentSubscription(db.collection('settings').doc('general'));
  const destinationOptions = destinations.map(_ => ({ label: _.name, value: _.id }));
  const [destinationId, setDestinationId] = useState(null);
  const [items, { set: setItems, updateAt: updateItemAt }] = useList(values.items || newItems || [{ productId: null, [types[type].quantityName]: 1, [types[type].opponentQuantityName]: 0, }]);
  const _deliveryType = deliveryType(generalSetting, { items, ...mapValues(pick(statedFields, ['shippingOptionValues', 'postalCode']), 'value') }, productsById, tenant.allowsMultiNeko);
  const [shippingItems, { set: setShippingItem, updateAt: updateShippingItemAt }] = useList(values.shippingItems || []);
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const isUnsubmittable =
    Object.values(statedFields).some(_ => !_.isValid) ||
    (!admin && items.length === 0) ||
    (!admin && items.some(_ => !_.isValid)) ||
    (admin && shippingItems.some(_ => !_.isValid));
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    toggleSubmitting(true);
    try {
      let _items;
      let hasCase;
      let itemsWithSku;
      let plans = null;
      if(!admin) {
        const products = await Promise.all(items.map(_ => getDocumentData(tenant.ref.collection('products').doc(_.productId))));
        const productsById = keyBy(products, 'id');
        const productsBySku = keyBy(products, 'sku');
        hasCase = items.some(_ => productsById[_.productId]?.isCase);
        const quantityName = types[type].quantityName;
        itemsWithSku = items.map((item) => {
          const { productId, [quantityName]: quantity, } = item;
          const product = productsById[productId];
          return {
            ...item,
            sku: product?.sku,
            quantity,
          };
        });
        _items = (await Promise.all(itemsWithSku.map(async (item) => {
          const { productId, [quantityName]: quantity, } = item;
          const product = productsById[productId];
          return (type === 'amazon' && !admin && product.isCase) ? (await Promise.all(product.items.map(async (_) => {
            const product = await getDocumentData(tenant.ref.collection('products').doc(_.productId));
            return {
              ..._,
              sku: product?.sku,
              quantity: quantity * _.quantity,
            };
          }))) : {
            ...item,
            sku: product?.sku,
            quantity,
          };
        }))).flat();
        if(type === 'amazon' && isNew) {
          const sender = omit(sendersById[statedFields.senderId.value], 'ref');
          try {
            const { data } = await fetchFulfillmentInboundShipmentPlan({
              tenantId: tenant.id,
              items: _items,
              sender,
              extraSellerId: selectedExtraSellerId,
            });
            plans = data;
          } catch(e) {
            const errorSkus = computeInboundShipmentErrorSkus(e);
            await batch(db, errorSkus || [], (batch, errorSku) => {
              const product = productsBySku[errorSku];
              product && batch.update(product.ref, {
                productTagIds: firebase.firestore.FieldValue.arrayUnion('inboundShippmentError'),
              });
            });
            throw e;
          }
          if(plans.length > 1 && hasCase) throw new Error('ケース商品が含まれていて納品プランが分かれてしまいました。商品数を減らして再度お試しください');

          if(plans.length > 1) {
            if(!window.confirm(`Amazonへの配送が複数に分かれるため、出荷依頼を複数に分けて登録します。よろしいですか？`)) return;
          }
        }
      }
      await props.onSubmit({
        ...mapValues(statedFields, 'value'),
        shippingItems: shippingItems.map(_ => omit(_, 'isValid')),
        ...(!admin && {
          type,
          hasCase,
          items: _items.map(_ => omit(_, 'isValid')),
          ...(type === 'amazon' && hasCase && { rawItems: itemsWithSku, }),
          ...(isNew && type === 'amazon' && { plans, extraSellerId: selectedExtraSellerId, }),
        }),
      });
    } catch(e) {
      toast.error(`エラーが発生しました (${e.message})`, { autoClose: false });
      throw e;
    } finally {
      toggleSubmitting(false);
    }
  };
  const onClickApplyDestination = () => {
    statedFields.setValues({
      ...pick(destinations.find(_ => _.id === destinationId), ['postalCode', 'prefecture', 'city', 'address1', 'address2', 'name', 'phone',]),
    });
    setDestinationId(null);
  };
  const targetProducts = useMemo(_ => (
    // セット品は実在庫を持たないため、手動の出荷依頼では選択させない
    (type === 'amazon' ? products.filter(_ => _.isAmazonProduct && (_.isCase || _.deliveryRoute === 'amazon' && !isEmpty(_.fnsku))) : products).filter(_ => !_.isSet)
  ), [type, products]);
  const memoizedItemFields = useMemo(_ => itemFields({ items, products, type, }), [items, products, type]);
  useEffect(() => {
    if(statedFields.postalCode == null) return;
    if(!statedFields.postalCode.isValid) return;

    (async () => {
      const [data] = await getAddressByPostalCode(statedFields.postalCode.value);
      data && statedFields.setValues(omit(data, 'address1'));
    })();
  }, [get(statedFields, ['postalCode', 'value'])]);

  return (
    <Modal className="position-relative" isOpen={isOpen} style={{ minWidth: '90vw', }}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            {types[type].label} {isNew ? '追加' : '編集'}
          </h5>
        </div>
      </div>
      <Form onSubmit={onSubmit}>
        <ModalBody className="overflow-auto" style={{ minHeight: 600, maxHeight: '70vh' }}>
          {
            !admin && type === 'amazon' && tenant.tenantOptionValues?.includes('multiSellers') && (
              <div className="mb-2" >
                <div className="text-muted small">ショップ</div>
                <div style={{ width: 200 }}>
                  <Select
                    options={extraSellerOptions}
                    value={extraSellerOptions.find(_ => _.value === selectedExtraSellerId) || null}
                    onChange={_ => setSelectedExtraSellerId(_?.value)}
                    className="w-100"
                  />
                </div>
              </div>
            )
          }
          {
            !admin && (
              <div>
                <ListForm
                  initialItem={_ => ({ [types[type].quantityName]: 1, [types[type].opponentQuantityName]: 0, })}
                  addButtonLabel="商品追加"
                  items={items}
                  renderItem={(item, itemIndex) => {
                    return (
                      <div className="card p-3">
                        <ItemForm
                          fields={memoizedItemFields}
                          index={itemIndex}
                          values={item}
                          product={productsById[item.productId]}
                          products={targetProducts}
                          tenant={tenant}
                          onChange={_ => updateItemAt(itemIndex, { ...item, ..._, })}
                          otherUncompletedShippingRequests={uncompletedShippingRequests.filter(_ => _.id !== values?.id)}
                          admin={admin}
                          disabled={type === 'amazon' && !isNew}
                        />
                      </div>
                    );
                  }}
                  renderFooterContent={(base) => {
                    const onClickAll = async () => {
                      const { data: items, } = await computeAmazonShippingRequestAllItems({
                        tenantId: tenant.id,
                        extraSellerId: selectedExtraSellerId,
                      });
                      setItems([]);
                      await new Promise(_ => setTimeout(_, 100));
                      setItems(items);
                    };
                    const onSubmitText = (values, { onClickClose }) => {
                      setItems(values.text?.split(/\r\n|\n|\r/)
                        .filter(_ => _)
                        .map(_ => _.split(/[ 　\t]+/))
                        .map(([sku, quantityString]) => {
                          const product = productsBySku[sku];
                          const quantity = numeral(quantityString).value();
                          return {
                            productId: product?.id,
                            sku: product?.sku,
                            goodQuantity: quantity,
                            quantity,
                          };
                        })
                        .filter(_ => _.productId)
                      );
                      onClickClose();
                    };
                    const textFields = {
                      text: {
                        type: 'text',
                        rows: 5,
                        hint: 'SKUと数量指定。SKUと数量の間はスペースかタブ。改行区切り。',
                      },
                    };

                    return (
                      <Fragment>
                        {base}
                        {
                          type === 'amazon' && (
                            <ProgressButton className="ml-1" process={onClickAll}>
                              <span className="fas fa-plus mr-1" />
                              一括納品依頼
                            </ProgressButton>
                          )
                        }
                        <ModalButton className="ml-1" Modal={ModelFormModal} modalProps={{ title: 'テキスト指定', fields: textFields, onSubmit: onSubmitText, submitLabel: '指定する', }}>
                          <span className="fas fa-plus mr-1" />
                          テキスト指定
                        </ModalButton>
                      </Fragment>
                    );
                  }}
                  onChange={setItems}
                  minItems={1}
                  disabled={type === 'amazon' && !isNew}
                />
              </div>
            )
          }
          {
            admin && type !== 'discard' && (
              <div className="mt-3 card p-3">
                <ListForm
                  addButtonLabel="伝票番号/箱サイズ 追加"
                  items={shippingItems}
                  renderItem={(shippingItem, shippingItemIndex) => {
                    return (
                      <div className="card p-3">
                        <ShippingItemForm
                          fields={shippingItemFields({ type, })}
                          index={shippingItemIndex}
                          values={shippingItem}
                          onChange={_ => updateShippingItemAt(shippingItemIndex, { ...shippingItem, ..._, })}
                        />
                      </div>
                    );
                  }}
                  onChange={setShippingItem}
                />
              </div>
            )
          }
          <div className="mt-3">
            {
              _deliveryType === 'neko' && !admin && (
                <div className="alert alert-warning">
                  ポスト便サイズの商品のため日時指定はできません。
                </div>
              )
            }
            {
              ['normal', 'return'].includes(type) && !admin && (
                <div className="my-3 d-flex justify-content-end align-items-end gap-3">
                  <Field
                    {...statedFields.isToBe}
                  />
                  <div className="d-flex gap-1 mb-2">
                    <div style={{ width: 200 }}>
                      <Select
                        options={destinationOptions}
                        value={destinationOptions.find(_ => _.value === destinationId) || null}
                        onChange={_ => setDestinationId(_?.value)}
                        className="w-100"
                        placeholder="配送先"
                      />
                    </div>
                    <Button onClick={onClickApplyDestination}>
                      適用
                    </Button>
                  </div>
                </div>
              )
            }
            {
              entries(omit(statedFields, 'isToBe')).map(([fieldName, fieldSetting]) => {
                if(['deliveryDate', 'deliveryTime'].includes(fieldName) && _deliveryType === 'neko') return;

                if(_deliveryType.match(/^yamato/)) {
                  if(fieldName === 'deliveryDate') {
                    return (
                      <Field
                        key={fieldName}
                        name={fieldName}
                        {...fieldSetting}
                        inputProps={{
                          minDate: addDays(new Date(), 4),
                        }}
                      />
                    );
                  }
                  if(fieldName === 'deliveryTime') {
                    const options = entries(pickBy(deliveryTimes, _ => ['yamato', 'all'].includes(_.type))).map(([k, _]) => ({ label: _.label, value: k }));
                    return (
                      <Field
                        key={fieldName}
                        name={fieldName}
                        {...fieldSetting}
                        options={options}
                      />
                    );
                  }
                }

                return (
                  <Field
                    key={fieldName}
                    name={fieldName}
                    {...fieldSetting}
                  />
                );
              })
            }
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable || isSubmitting}>保存</Button>
        </ModalFooter>
      </Form>
      <OverlayLoading isOpen={isSubmitting} label="処理中..." />
    </Modal>
  );
};

function ItemForm (props) {
  const { index: itemIndex, otherUncompletedShippingRequests, fields, values, tenant, product, onChange, admin = false, disabled = false, products, } = props;
  const statedFields = useFormState(values, fields, values);
  const [query, setQuery] = useState('');
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const { id, ref, sku, code, name, asin, fnsku, barcodeInfo, } = product;
      return [id, name, sku, code, asin, fnsku, barcodeInfo].join(' ').includes(query);
    }).slice(0, 100);
  }, [products, query]);
  const dbProducts = useDocumentsFetch([tenant.ref.collection('products').doc(statedFields.productId.value ?? 'dummy'), ...filteredProducts.filter(_ => _.id !== statedFields.productId.id).map(_ => _.ref)], [filteredProducts, statedFields.productId.value]);
  const productOptions = useMemo(_ => {
    return dbProducts.filter(product => !product.hides).map((product) => {
      const { goodInventory, ngInventory, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation } = computeInventory(product, otherUncompletedShippingRequests);
      const { id, ref, sku, code, name, asin, fnsku, barcodeInfo, } = product;
      return {
        value: product.id,
        label: (
          <ProductSelectOptionLabel product={product} goodInventoryWithoutAllocation={goodInventoryWithoutAllocation} waitingToShippingGoodCount={waitingToShippingGoodCount}/>
        ),
        data: [id, name, sku, code, asin, fnsku, barcodeInfo].join(' '),
      };
    })
  }, [dbProducts]);

  return (
    <div>
      <div className="d-flex">
        <div className="mr-2" style={{ minWidth: 500 }}>
          <Select
            className="w-100"
            placeholder={'商品名、SKU、ASIN、FNSKU、JANで検索できます(検索結果は最大100個まで表示されます)'}
            noOptionsMessage={_ => '検索することで商品が表示されます'}
            options={productOptions}
            value={productOptions.find(_ => _.value === statedFields.productId.value) || null}
            onChange={_ => _ && statedFields.productId.setValue(_.value)}
            onInputChange={_ => setQuery(_)}
            styles={{
              valueContainer: _ => ({ ..._, height: 60 }),
              singleValue: _ => ({ ..._, top: 0, transform: '' }),
            }}
            isDisabled={disabled}
            components={{ MenuList }}
            filterOption={(o, q) => o.data.data.includes(q)}
          />
        </div>
        {
          entries(omit(statedFields, 'productId')).map(([fieldName, fieldSetting], i) => (
            <div className="mr-2">
              <Field
                key={fieldName}
                name={fieldName}
                {...fieldSetting}
                readOnly={_ => disabled}
                {...(itemIndex === 0 && i === 0 && { inputProps: { autoFocus: true, } })}
              />
            </div>
          ))
        }
      </div>
    </div>
  );
}
